'use client';
import React, { useState, useMemo } from 'react';
import CloudImage from '@/components/CloudImage';
import CustomGrid from '@/components/CustomGrid';
import ButtonContainer from '@/components/ButtonContainer';
import VehicleClassifierPopup from '@/components/vehicle-classifier/VehicleClassifierPopup';
import MediaContainer from '@/components/MediaContainer';
import MarkdownText from '@/components/MarkdownText';
import BookingButton from '@/components/BookingButton';
import Icon from '@/components/Icon';
import { FaCalendarCheck } from 'react-icons/fa';

export default function PackageSection({
  packageInfo = {
    title: '',
    subtitle: '',
    imagePosition: 'left', // "left" or "right"
    priceRange: '',
    includes: '',
    imageUrl: '',
    imageAlt: '',
    description: '',
    benefits: [],
    additionalInfo: [],
    brand: '', // e.g. "pro", "max-mf", "crystal"
    slug: '', // used for image path
    servicePricing: [], // array of { slug, price }
  },

  paintCorrectionLevel = {
    paintEnhancement: {
      description:
        'Paint Enhancement is a process that focuses on improving the overall appearance of your vehicle’s paint. It involves a single-stage machine polishing to remove light surface imperfections, such as minor swirl marks and oxidation. This process significantly enhances gloss and clarity, making your car look vibrant and well-maintained.',
      idealFor:
        'Newer models with less than 500 miles, and for vehicles with minor imperfections that only need a visual boost.',
    },
    level1: {
      description:
        'Level 1 Paint Correction is a more intensive process that targets moderate paint defects. This process involves a two-stage machine polishing technique to remove medium-level swirl marks, scratches, and oxidation. It restores the paint’s depth and shine, giving your car a like-new appearance. This is our most common correction.',
      idealFor:
        'Everyday drivers looking to correct moderate imperfections and achieve a more refined finish.',
    },
    level2: {
      description:
        'Level 2 Paint Correction is the most comprehensive correction process. It involves a multi-stage machine polishing technique to eliminate severe paint defects, such as deep swirl marks, scratches, and significant oxidation. This process restores the paint to its maximum potential, providing an unparalleled level of clarity and gloss.',
      idealFor:
        'Show cars or owners seeking a flawless finish. Perfect for vehicles with significant imperfections or for enthusiasts demanding the highest level of paint refinement.',
    },
  },
}) {
  const {
    title = '',
    subtitle = '',
    imagePosition = 'left',
    priceRange = '',
    includes = '',
    imageUrl = '',
    imageAlt = '',
    description = '',
    benefits = [],
    additionalInfo = [],
    brand = '',
    slug = '',
    servicePricing = [],
  } = packageInfo;

  // Determine if this brand should skip the paint-correction step
  const isMaxMF = brand.toLowerCase() === 'max-mf';

  // Dropdown state
  const [vehicleSize, setVehicleSize] = useState('');
  const [paintCorrection, setPaintCorrection] = useState('');

  // If max-mf, force correction to "none"
  const effectivePaintCorrection = isMaxMF ? 'none' : paintCorrection;
  const bothSelected = Boolean(
    vehicleSize && brand && (isMaxMF || paintCorrection),
  );

  // Layout classes
  const isRight = imagePosition === 'right';
  const imageOrderClasses = isRight
    ? 'order-first laptop:order-last'
    : 'order-first';
  const textOrderClasses = isRight
    ? 'order-last laptop:order-first'
    : 'order-last';

  // Build the slug and look up pricing
  const computedSlug = useMemo(() => {
    return bothSelected
      ? `${brand}-${vehicleSize}-${effectivePaintCorrection}`
      : '';
  }, [brand, vehicleSize, effectivePaintCorrection, bothSelected]);

  const matchedPricing = useMemo(() => {
    return bothSelected
      ? servicePricing.find((p) => p.slug === computedSlug)
      : null;
  }, [servicePricing, bothSelected, computedSlug]);

  const displayPrice = matchedPricing?.price ?? 'Not available';

  // Paint correction details lookup
  const paintCorrectionMapping = {
    'paint-enhancement': 'paintEnhancement',
    'level-1': 'level1',
    'level-2': 'level2',
  };
  const selectedCorrection =
    !isMaxMF && paintCorrection && paintCorrectionMapping[paintCorrection]
      ? paintCorrectionLevel[paintCorrectionMapping[paintCorrection]]
      : null;

  return (
    <div className="w-full">
      <div className="mx-auto">
        <CustomGrid className="bg-secondary/10 h-full items-center gap-8 rounded-sm p-8 shadow-xl">
          {/* Image */}
          <div className={imageOrderClasses}>
            <MediaContainer className="tablet:aspect-4/3 laptop:aspect-3/4 aspect-square overflow-hidden rounded">
              <CloudImage
                publicId={`mad-web-app/serv-images/${slug}`}
                alt={imageAlt || title}
                fill
              />
            </MediaContainer>
          </div>

          {/* Text + Form */}
          <div className={`${textOrderClasses} space-y-4`}>
            <h2 className="tablet:text-3xl text-2xl font-bold text-gray-900">
              {title}
            </h2>
            <p className="text-lg font-semibold text-red-500">{subtitle}</p>
            <p className="text-lg font-semibold text-gray-800">{priceRange}</p>

            <MarkdownText>{description}</MarkdownText>
            <MarkdownText>{includes}</MarkdownText>

            <p className="mt-4 mb-2 font-semibold">Benefits</p>
            <MarkdownText>
              {benefits.map((b) => `- ${b}`).join('\n')}
            </MarkdownText>

            {additionalInfo.length > 0 && (
              <>
                <p className="mt-4 mb-2 font-semibold">Additional Benefits</p>
                <MarkdownText>
                  {additionalInfo.map((info) => `- ${info}`).join('\n')}
                </MarkdownText>
              </>
            )}

            {/* Selections */}
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="vehicleSize"
                  className="mb-1 block text-sm font-semibold"
                >
                  Vehicle Size
                </label>
                <select
                  id="vehicleSize"
                  className="bg-primary-foreground w-full rounded-sm border border-gray-300 px-3 py-2"
                  value={vehicleSize}
                  onChange={(e) => setVehicleSize(e.target.value)}
                >
                  <option value="">Select one…</option>
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="x-large">X-Large</option>
                </select>
              </div>

              {!isMaxMF && (
                <div>
                  <label
                    htmlFor="paintCorrection"
                    className="mb-1 block text-sm font-semibold"
                  >
                    Paint Correction
                  </label>
                  <select
                    id="paintCorrection"
                    className="bg-primary-foreground w-full rounded-sm border border-gray-300 px-3 py-2"
                    value={paintCorrection}
                    onChange={(e) => setPaintCorrection(e.target.value)}
                  >
                    <option value="">Select one…</option>
                    <option value="paint-enhancement">Paint Enhancement</option>
                    <option value="level-1">Level 1</option>
                    <option value="level-2">Level 2</option>
                  </select>
                </div>
              )}
            </div>

            {/* Correction Details */}
            {!isMaxMF && selectedCorrection && (
              <div className="mb-4 space-y-2 rounded-sm border border-gray-300 bg-gray-50 p-4">
                <p className="text-sm font-semibold">
                  Description:{' '}
                  <span className="font-normal">
                    {selectedCorrection.description}
                  </span>
                </p>
                <p className="text-sm font-semibold">
                  Ideal For:{' '}
                  <span className="font-normal">
                    {selectedCorrection.idealFor}
                  </span>
                </p>
              </div>
            )}

            {/* Price Display */}
            {bothSelected ? (
              <div className="text-2xl font-bold text-green-600">
                Price: ${displayPrice}
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Please select required options to get the price.
              </p>
            )}

            {/* CTAs */}
            <ButtonContainer>
              <BookingButton>
                <Icon icon={FaCalendarCheck} className="mr-2" />
                Book Now
              </BookingButton>
              <VehicleClassifierPopup buttonVariant="tertiary" />
            </ButtonContainer>
          </div>
        </CustomGrid>
      </div>
    </div>
  );
}
